import React, { useState } from "react";
import { Calendar } from "@/components/ui/calendar";
import { CalendarIcon } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { toast } from "@/components/ui/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { calculateWorkingDays } from "@/lib/dateUtils";
import { LeaveBalance } from "@/types/timeoff";

interface LogTimeOffModalProps {
  isOpen: boolean;
  onClose: () => void;
  leaveBalances: LeaveBalance[];
  onSubmit: (values: TimeOffFormValues) => Promise<void>;
  requiresApproval?: boolean;
}

interface TimeOffFormValues {
  leaveTypeVersionId: number;
  startDate: Date;
  endDate: Date;
  hours?: number;
  reason?: string;
}

const formSchema = z
  .object({
    leaveTypeVersionId: z.number({
      required_error: "Please select a leave type",
    }),
    startDate: z.date({
      required_error: "Start date is required",
    }),
    endDate: z
      .date({
        required_error: "End date is required",
      })
      .refine((date) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return date.getTime() >= today.getTime();
      }, {
        message: "End date cannot be in the past",
      }),
    hours: z.number().optional(),
    reason: z.string().optional(),
  })
  .refine((data) => data.endDate >= data.startDate, {
    message: "End date must be after start date",
    path: ["endDate"],
  });

const LogTimeOffModal: React.FC<LogTimeOffModalProps> = ({
  isOpen,
  onClose,
  leaveBalances,
  onSubmit,
  requiresApproval = false,
}) => {
  const { user } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      startDate: new Date(),
      endDate: new Date(),
      hours: undefined,
      reason: "",
    },
  });

  const { watch, setValue, reset } = form;
  const startDate = watch("startDate");
  const endDate = watch("endDate");
  const selectedLeaveTypeId = watch("leaveTypeVersionId");

  const selectedLeaveBalance = leaveBalances.find(
    (balance) => balance.leaveTypeVersionId === selectedLeaveTypeId,
  );

  const workingDays =
    startDate && endDate ? calculateWorkingDays(startDate, endDate) : 0;
  const requestedHours = workingDays * 8;

  const canSubmit =
    selectedLeaveBalance &&
    (selectedLeaveBalance.balance === 999999 ||
      requestedHours <= selectedLeaveBalance.balance);

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      setIsSubmitting(true);

      await onSubmit({
        ...values,
        hours: requestedHours,
      });

      toast({
        title: requiresApproval
          ? "Time off request submitted"
          : "Time off logged",
        description: requiresApproval
          ? "Your request has been submitted for approval"
          : "Time off has been successfully logged",
      });

      handleClose();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to submit time off request",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            {requiresApproval ? "Request Time Off" : "Log Time Off"}
          </DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="space-y-4"
          >
            <FormField
              control={form.control}
              name="leaveTypeVersionId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Leave Type</FormLabel>
                  <Select
                    onValueChange={(value) => field.onChange(parseInt(value))}
                    defaultValue={field.value?.toString()}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a leave type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {leaveBalances.map((balance) => (
                        <SelectItem
                          key={balance.leaveTypeVersionId}
                          value={balance.leaveTypeVersionId.toString()}
                        >
                          {balance.policyDisplayName} (
                          {balance.balance < 999999
                            ? `${balance.balance} hours available`
                            : "Unlimited"}
                          )
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="startDate"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>Start Date</FormLabel>
                  <Popover>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          variant={"outline"}
                          className={cn(
                            "w-full pl-3 text-left font-normal",
                            !field.value && "text-muted-foreground",
                          )}
                        >
                          {field.value ? (
                            format(field.value, "dd/MM/yyyy")
                          ) : (
                            <span>Pick a date</span>
                          )}
                          <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent
                      className="w-auto p-0 pointer-events-auto"
                      align="start"
                    >
                      <Calendar
                        mode="single"
                        selected={field.value}
                        defaultMonth={
                          field.value ? new Date(field.value) : undefined
                        }
                        onSelect={field.onChange}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="endDate"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>End Date</FormLabel>
                  <Popover>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          variant={"outline"}
                          className={cn(
                            "w-full pl-3 text-left font-normal",
                            !field.value && "text-muted-foreground",
                          )}
                        >
                          {field.value ? (
                            format(field.value, "dd/MM/yyyy")
                          ) : (
                            <span>Pick a date</span>
                          )}
                          <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent
                      className="w-auto p-0 pointer-events-auto"
                      align="start"
                    >
                      <Calendar
                        mode="single"
                        selected={field.value}
                        defaultMonth={
                          field.value ? new Date(field.value) : undefined
                        }
                        onSelect={field.onChange}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  <FormMessage />
                </FormItem>
              )}
            />

            {requiresApproval && (
              <FormField
                control={form.control}
                name="reason"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Reason (Optional)</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Please provide any additional details about your time off request"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {workingDays > 0 && selectedLeaveBalance && (
              <FormDescription className="text-sm">
                This request is for {workingDays} working days ({requestedHours}{" "}
                hours)
                {selectedLeaveBalance.balance !== 999999 && (
                  <>
                    . You have {selectedLeaveBalance.balance} hours available.
                    {requestedHours > selectedLeaveBalance.balance && (
                      <span className="text-red-500">
                        {" "}
                        This exceeds your available balance.
                      </span>
                    )}
                  </>
                )}
              </FormDescription>
            )}

            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting || !canSubmit}>
                {isSubmitting
                  ? "Submitting..."
                  : requiresApproval
                    ? "Submit Request"
                    : "Log Time Off"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default LogTimeOffModal;
